import { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { getChatAttachment } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { parseAiMarkdown } from '../markdown/ai-marked';

export type ChatAttachmentChip = {
  id: string;
  fileName: string;
  mimeType?: string;
  status?: string;
};

function AttachmentIcon({ mimeType }: { mimeType?: string }) {
  if (mimeType?.startsWith('image/')) {
    return <ImageIcon className="h-3.5 w-3.5 shrink-0" />;
  }
  return <FileText className="h-3.5 w-3.5 shrink-0" />;
}

interface MessageAttachmentsProps {
  attachments: ChatAttachmentChip[];
  /** Style chips for user bubble (on primary bg) */
  onPrimary?: boolean;
}

export function MessageAttachments({ attachments, onPrimary = false }: MessageAttachmentsProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openAttachment = attachments.find((a) => a.id === openId);

  useEffect(() => {
    if (!openId) {
      setMarkdown(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMarkdown(null);
    getChatAttachment(openId)
      .then((asset) => {
        if (cancelled) return;
        setTitle(asset.fileName);
        setMarkdown(asset.markdown ?? asset.markdownPreview ?? '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load attachment');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openId]);

  if (attachments.length === 0) return null;

  return (
    <>
      <ul className="mb-2 flex flex-wrap gap-1.5">
        {attachments.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => setOpenId(a.id)}
              className={cn(
                'inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border px-2 py-1 text-xs transition-colors',
                onPrimary
                  ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20'
                  : 'border-border bg-background/80 text-foreground hover:bg-muted'
              )}
              title={`View extracted content: ${a.fileName}`}
            >
              <AttachmentIcon mimeType={a.mimeType} />
              <span className="truncate">{a.fileName}</span>
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={openId != null} onOpenChange={(open) => !open && setOpenId(null)}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-3 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{title || openAttachment?.fileName}</DialogTitle>
            <DialogDescription>Extracted document content</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm">
            {loading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            )}
            {error && <p className="text-destructive">{error}</p>}
            {!loading && !error && markdown != null && (
              <div
                className="chat-markdown"
                dangerouslySetInnerHTML={{ __html: parseAiMarkdown(markdown) }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Strip legacy inline attachment dumps from stored message content for display. */
export function stripAttachedDocumentsForDisplay(content: string): string {
  return content.replace(/<attached_documents>[\s\S]*?<\/attached_documents>\s*/gi, '').trim();
}
